<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  McSelect,
  McOption,
  McInputDate,
  McMenu,
  McMonthYearPicker,
  McTooltip
} from "@maersk-global/mds-components-core";
import { useFiltersStore } from "@/store";
import {
  useApplyFilter,
  FilterDefinition,
  FilterType,
  FilterTypeEnum,
  SelectDateRangeOption,
  FilterSelection
} from "@/features/filters";

const props = defineProps<{
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
  disabled?: boolean;
}>();

const {
  key: filterKey,
  label: filterLabel = "",
  hiddenlabel,
  placeholder = "",
  options: staticOptions = [],
  value: defaultValue,
  groupLabel,
  groupId,
  sectionLabel,
  labelTooltip = false,
  fromLabel = "FROM",
  toLabel = "TO",
  fromPlaceholder = "YYYY-MM-DD",
  toPlaceholder = "YYYY-MM-DD",
  fromMonthYearPlaceholder = "YYYY-MM",
  toMonthYearPlaceholder = "YYYY-MM",
  fromMonthYearLabel = "FROM",
  toMonthYearLabel = "TO",
  min,
  max,
  disableTyping = false,
  dateInputWidth
} = props.filter;

const { t, locale } = useI18n();

// An empty `fromLabel`/`toLabel` drops the label element entirely (mc-input-date has no
// `hiddenlabel`); a non-empty label — including risk-management's " " — is unaffected.
const fromLabelText = computed(() => (fromLabel ? t(fromLabel) : undefined));
const toLabelText = computed(() => (toLabel ? t(toLabel) : undefined));

// Same opt-out for the month-year variant (Emissions Peer Benchmarking).
const fromMonthYearLabelText = computed(() => (fromMonthYearLabel ? t(fromMonthYearLabel) : undefined));
const toMonthYearLabelText = computed(() => (toMonthYearLabel ? t(toMonthYearLabel) : undefined));

// Without a visible label the placeholder is the only name the field has, so mirror it
// into aria-label rather than leaving the input unnamed.
const fromInputAriaLabel = computed(() => (fromLabelText.value ? undefined : t(fromPlaceholder)));
const toInputAriaLabel = computed(() => (toLabelText.value ? undefined : t(toPlaceholder)));
const fromMonthYearAriaLabel = computed(() => (fromMonthYearLabelText.value ? undefined : t(fromMonthYearPlaceholder)));
const toMonthYearAriaLabel = computed(() => (toMonthYearLabelText.value ? undefined : t(toMonthYearPlaceholder)));

const dateInputStyle = computed(() =>
  dateInputWidth ? { width: dateInputWidth, minWidth: dateInputWidth } : undefined
);

const filtersStore = useFiltersStore();
const { filtersApplied, panelSelectedFilters } = filtersStore;
const { applyFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const zone = props.zonePrefix || "default";
const selectedFilterOption = ref<FilterSelection>(null);
const previousDates = ref({ startDate: "", endDate: "" });

const selectedFilters = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  const selected = selectedFilterOption.value;

  return selected?.value === SelectDateRangeOption.CUSTOM_MONTH_RANGE ||
    selected?.value === SelectDateRangeOption.CUSTOM_RANGE
    ? selectedFilterOption.value
    : source?.[zone]?.[filterKey] || defaultValue || {};
});

const customDateRangeMap = ref<{ [zone: string]: { startDate: string; endDate: string } }>({});
if (!customDateRangeMap.value[zone]) {
  customDateRangeMap.value[zone] = { startDate: "", endDate: "" };
}
const customDateRange = computed({
  get: () => customDateRangeMap.value[zone],
  set: (val) => {
    customDateRangeMap.value[zone] = val;
  }
});

const optionsMap = ref<{ [zone: string]: any[] }>({});
if (!optionsMap.value[zone]) {
  optionsMap.value[zone] = staticOptions.map((opt) => ({ ...opt }));
}
const options = computed(() => optionsMap.value[zone]);

const syncInitialCustomDate = () => {
  const stored = selectedFilters.value;
  if (stored?.value === SelectDateRangeOption.CUSTOM_RANGE && stored?.dateRange) {
    customDateRangeMap.value[zone] = {
      startDate: stored.dateRange.startDate || "",
      endDate: stored.dateRange.endDate || ""
    };
  }
};

syncInitialCustomDate();

watch(
  () => selectedFilters.value?.value,
  (newVal) => {
    if (
      (newVal === SelectDateRangeOption.CUSTOM_RANGE || newVal === SelectDateRangeOption.CUSTOM_MONTH_RANGE) &&
      selectedFilters.value?.dateRange
    ) {
      customDateRangeMap.value[zone] = {
        startDate: selectedFilters.value.dateRange.startDate || "",
        endDate: selectedFilters.value.dateRange.endDate || ""
      };
      previousDates.value = { ...customDateRangeMap.value[zone] };
    }
  },
  { immediate: true }
);

const setCustomDateFilter = (customOption, customType, newVal) => {
  applyFilter(filterKey, {
    ...customOption,
    filterKey,
    value: customType,
    label: customOption?.label || "Custom",
    dateRange: { ...newVal },
    filterType: props.filterType,
    ...(groupId ? { groupId } : {}),
    ...(groupLabel ? { groupLabel } : {}),
    ...(sectionLabel ? { sectionLabel } : {}),
    ...(filterLabel ? { filterLabel } : {})
  });

  // Update previous dates after applying filter
  previousDates.value = { ...newVal };
};

watch(
  customDateRange,
  (newVal) => {
    if (!newVal.startDate || !newVal.endDate) return;

    const customType = selectedFilters.value?.value;
    // Check if dates have changed by comparing with existing stored filter
    const hasDateChanged =
      previousDates.value.startDate !== newVal.startDate || previousDates.value.endDate !== newVal.endDate;

    if (customType === SelectDateRangeOption.CUSTOM_RANGE || customType === SelectDateRangeOption.CUSTOM_MONTH_RANGE) {
      const customOption = options.value.find((o) => o.value === customType);

      if (hasDateChanged) {
        setCustomDateFilter(customOption, customType, newVal);
      }
    }
  },
  { deep: true }
);

const onChange = (e: Event) => {
  const value = (e.target as HTMLSelectElement).value;
  const selectedOption = options.value.find((o) => o.value === value);
  if (!selectedOption) return;

  selectedFilterOption.value = selectedOption;

  if (
    selectedOption.value === SelectDateRangeOption.CUSTOM_MONTH_RANGE ||
    selectedOption.value === SelectDateRangeOption.CUSTOM_RANGE
  ) {
    return;
  }

  applyFilter(filterKey, {
    ...selectedOption,
    filterKey,
    filterType: props.filterType,
    ...(groupId ? { groupId } : {}),
    ...(groupLabel ? { groupLabel } : {}),
    ...(sectionLabel ? { sectionLabel } : {}),
    ...(filterLabel ? { filterLabel } : {})
  });
};

const handleFromMonthYear = (e) => {
  const { month, year } = e.detail.value;
  customDateRangeMap.value[zone].startDate = `${year}-${String(month + 1).padStart(2, "0")}-01`;
};

const handleToMonthYear = (e) => {
  const { month, year } = e.detail.value;
  const lastDay = new Date(year, month + 1, 0).getDate();
  customDateRangeMap.value[zone].endDate =
    `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
};

const minMaxMonthYear = computed(() => {
  const start = customDateRangeMap.value[zone].startDate;
  const end = customDateRangeMap.value[zone].endDate;

  let startYear = null,
    startMonth = null,
    endYear = null,
    endMonth = null;
  if (start) {
    [startYear, startMonth] = start.split("-").map(Number);
    startMonth = typeof startMonth === "number" ? startMonth - 1 : null;
  }
  if (end) {
    [endYear, endMonth] = end.split("-").map(Number);
    endMonth = typeof endMonth === "number" ? endMonth - 1 : null;
  }

  return {
    startDateMin: null,
    startDateMax: end ? { month: endMonth, year: endYear } : null,
    endDateMin: start ? { month: startMonth, year: startYear } : null,
    endDateMax: null
  };
});

const minMaxDate = computed(() => {
  const start = customDateRangeMap.value[zone].startDate;
  const end = customDateRangeMap.value[zone].endDate;

  const startDateMin = typeof min === "string" && min ? min : null;
  const endDateMax = typeof max === "string" && max ? max : null;

  return {
    startDateMin,
    startDateMax: end || null,
    endDateMin: start || startDateMin,
    endDateMax
  };
});
</script>

<template>
  <div :key="locale" class="select-daterange-filter">
    <div
      class="select-daterange-filter__date-select-container"
      :style="{ width: props.filter.width, minWidth: props.filter.width }"
    >
      <mc-tooltip fit="small">
        <mc-select
          :key="locale"
          slot="trigger"
          :name="filterKey"
          fit="small"
          :label="t(filterLabel)"
          :hiddenlabel="hiddenlabel"
          :placeholder="t(placeholder)"
          :value="selectedFilters?.value"
          :disabled="props.disabled || undefined"
          @input="onChange"
        >
          <mc-option
            v-for="option in options"
            :key="option.value"
            :value="option.value"
            :disabled="option.disabled || undefined"
          >
            {{ t(option.label) }}
          </mc-option>
        </mc-select>
        <span v-if="labelTooltip && selectedFilters?.value">{{ t(filterLabel) }}</span>
      </mc-tooltip>
    </div>

    <div v-if="selectedFilters?.value === SelectDateRangeOption.CUSTOM_RANGE" class="custom-date-range">
      <mc-input-date
        v-model="customDateRange.startDate"
        class="custom-date-range--input"
        :style="dateInputStyle"
        fit="small"
        :yearcap="20"
        :hiddenLabel="fromLabelText ? false : true"
        :label="fromLabelText"
        :aria-label="fromInputAriaLabel"
        :placeholder="t(fromPlaceholder)"
        name="from-date"
        :min="minMaxDate.startDateMin"
        :max="minMaxDate.startDateMax"
        :disabled="props.disabled || undefined"
        v-on="disableTyping ? { keydown: (e) => e.preventDefault() } : {}"
      />
      <mc-input-date
        v-model="customDateRange.endDate"
        class="custom-date-range--input"
        :style="dateInputStyle"
        fit="small"
        :yearcap="20"
        :hiddenLabel="toLabelText ? false : true"
        :label="toLabelText"
        :aria-label="toInputAriaLabel"
        :placeholder="t(toPlaceholder)"
        name="to-date"
        :min="minMaxDate.endDateMin"
        :max="minMaxDate.endDateMax"
        :disabled="props.disabled || undefined"
        v-on="disableTyping ? { keydown: (e) => e.preventDefault() } : {}"
      />
    </div>

    <div v-if="selectedFilters?.value === SelectDateRangeOption.CUSTOM_MONTH_RANGE" class="custom-date-range">
      <mc-menu trigger="click">
        <mc-input
          slot="trigger"
          :style="dateInputStyle"
          fit="small"
          :placeholder="t(fromMonthYearPlaceholder)"
          :label="fromMonthYearLabelText"
          :aria-label="fromMonthYearAriaLabel"
          trailingicon="calendar"
          :value="customDateRange.startDate ? customDateRange.startDate.slice(0, 7) : ''"
          :disabled="props.disabled || undefined"
          readonly
        ></mc-input>
        <mc-month-year-picker
          :yearcap="20"
          fit="small"
          :min="minMaxMonthYear.startDateMin"
          :max="minMaxMonthYear.startDateMax"
          preventinitialeventdispatch
          @monthyearselected="handleFromMonthYear"
        >
        </mc-month-year-picker>
      </mc-menu>
      <mc-menu trigger="click">
        <mc-input
          slot="trigger"
          :style="dateInputStyle"
          fit="small"
          :placeholder="t(toMonthYearPlaceholder)"
          :label="toMonthYearLabelText"
          :aria-label="toMonthYearAriaLabel"
          trailingicon="calendar"
          :value="customDateRange.endDate ? customDateRange.endDate.slice(0, 7) : ''"
          :disabled="props.disabled || undefined"
          readonly
        ></mc-input>
        <mc-month-year-picker
          :yearcap="20"
          fit="small"
          :min="minMaxMonthYear.endDateMin"
          :max="minMaxMonthYear.endDateMax"
          preventinitialeventdispatch
          @monthyearselected="handleToMonthYear"
        >
        </mc-month-year-picker>
      </mc-menu>
    </div>
  </div>
</template>

<style scoped lang="scss">
.select-daterange-filter {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 8px;

  &__date-select-container {
    min-width: 150px;
  }
}

.custom-date-range {
  display: flex;
  flex-direction: row;
  gap: 4px;

  &--input {
    width: 150px;
    min-width: 150px;
  }
}
</style>
